import React from 'react';
import { DSButton, DSCard, DSCardFooterButton, DSIcon, DSBadge } from '../../design-system/primitives';
import { money, signedMoney, formatWhen, formatMonth, ordinal } from './helpers';
import { CREDIT_COST } from '../../hooks/useMyMembership';
import BadgeIcon from '../../components/BadgeIcon';

// The landing tab, in frequency order. Adding a dollar to a machine is the most
// used action in the whole application, so it sits above everything that is not
// the balance it spends.

function BalanceHero({ balance, isNegative, tabEnabled, tabLimit }) {
  const limitLabel = tabLimit === null || tabLimit === undefined
    ? 'no limit'
    : `$${parseFloat(tabLimit).toFixed(2)} limit`;

  const note = !isNegative
    ? 'Spend it on the machine, on a guest pass.'
    : tabEnabled
      ? `You are on your tab. Play carries on up to your ${limitLabel}. Settle up with Jeremy or Courtney next time you are in.`
      : 'Purchases are paused. Ask Jeremy or Courtney to add credit and settle up.';

  return (
    <section className="mm-hero">
      <div className="mm-hero__head">
        <p className={`mm-hero__eyebrow ${isNegative ? 'mm-hero__eyebrow--warn' : ''}`}>
          {isNegative ? 'Outstanding tab' : 'Credit balance'}
        </p>
        {isNegative && (tabEnabled
          ? <DSBadge variant="warning" size="sm">Tab active · {limitLabel}</DSBadge>
          : <DSBadge variant="error" size="sm">Purchases blocked</DSBadge>
        )}
      </div>
      <p className={`mm-hero__amount ${isNegative ? 'mm-hero__amount--negative' : ''}`}>
        {money(balance)}
      </p>
      <p className="mm-hero__note">{note}</p>
    </section>
  );
}

function MachineCard({ device, balance, purchasesBlocked, busy, onAdd }) {
  const bay = device.location || device.bay || null;

  if (!device.isOnline) {
    return (
      <div className="mm-machine mm-machine--offline">
        <div className="mm-machine__status">
          <span className="mm-dot mm-dot--muted" aria-hidden="true" />
          <span className="mm-machine__status-text">Machine offline</span>
          {bay && <span className="mm-machine__bay">{bay}</span>}
        </div>
        <p className="mm-machine__name mm-machine__name--muted">{device.device_name}</p>
        <p className="mm-machine__body">
          This machine is not accepting credit right now.
        </p>
      </div>
    );
  }

  return (
    <div className="mm-machine">
      <div className="mm-machine__status">
        <span className="mm-dot mm-dot--ok" aria-hidden="true" />
        <span className="mm-machine__status-text mm-machine__status-text--ok">Online</span>
        {bay && <span className="mm-machine__bay">{bay}</span>}
      </div>
      <p className="mm-machine__name">{device.device_name}</p>
      <DSButton
        className="mm-machine__button"
        appearance="solid"
        tone="primary"
        disabled={busy}
        leadingIcon={<DSIcon name="plus" size="sm" />}
        onClick={() => onAdd(device)}
      >
        {busy ? 'Adding credit...' : `Add $${CREDIT_COST.toFixed(0)} credit`}
      </DSButton>
      <p className="mm-machine__foot">
        {purchasesBlocked
          ? 'Top up your balance to add credit'
          : `Comes out of your ${money(balance)} balance`}
      </p>
    </div>
  );
}

function HomeTab({
  currentUser,
  membershipSettings,
  onboardingSteps,
  isMember,
  hasApplication,
  balance,
  isNegative,
  tabEnabled,
  purchasesBlocked,
  devices,
  devicesLoading,
  transactions,
  transactionsLoading,
  showAllActivity,
  onToggleActivity,
  badges,
  busy,
  onAddCredit,
  onOpenPurchase,
  onOpenBadge,
  onGoProfile,
  onGoStanding
}) {
  const statusTitle = isMember
    ? 'Membership active'
    : hasApplication ? 'Application in progress' : 'Visiting as a guest';

  const doneCount = onboardingSteps.filter((step) => step.done).length;
  const renewalDay = membershipSettings?.renewal_day_of_month;
  const statusNote = isMember
    ? [
        renewalDay ? `Renews on the ${ordinal(renewalDay)}` : null,
        membershipSettings?.auto_renewal ? 'auto renewal on' : 'auto renewal off'
      ].filter(Boolean).join(' · ')
    : hasApplication
      ? `${doneCount} of ${onboardingSteps.length} steps done. See what happens next.`
      : 'See what membership includes and how to apply.';

  const visibleActivity = showAllActivity ? transactions : transactions.slice(0, 3);
  const latestBadge = badges[0];
  const olderBadges = badges.slice(1, 4);

  return (
    <>
      <button type="button" className="mm-status-row" onClick={onGoProfile}>
        <span className={`mm-dot ${isMember ? 'mm-dot--ok' : 'mm-dot--pending'}`} aria-hidden="true" />
        <span className="mm-status-row__main">
          <span className="mm-status-row__title">{statusTitle}</span>
          <span className="mm-status-row__note">{statusNote}</span>
        </span>
        <DSIcon name="chevronRight" size="sm" tone="muted" />
      </button>

      <BalanceHero
        balance={balance}
        isNegative={isNegative}
        tabEnabled={tabEnabled}
        tabLimit={currentUser.tab_limit}
      />

      {/* Not a card. Each machine below is its own card, and nesting one card inside
          another only adds an edge to look past - so this is a labelled group. */}
      <section className="mm-group">
        <div className="mm-group__head">
          <p className="mm-group__label">On credit right now</p>
          <span className="mm-live"><span className="mm-dot mm-dot--ok mm-dot--pulse" aria-hidden="true" />Live</span>
        </div>
        {devicesLoading ? (
          <p className="mm-empty">Checking which machines are up...</p>
        ) : devices.length === 0 ? (
          <p className="mm-empty">No machines are taking credit right now.</p>
        ) : (
          <div className="mm-machines">
            {devices.map((device) => (
              <MachineCard
                key={device.id}
                device={device}
                balance={balance}
                purchasesBlocked={purchasesBlocked}
                busy={!!busy[`device:${device.id}`]}
                onAdd={onAddCredit}
              />
            ))}
          </div>
        )}
      </section>

      {latestBadge && (
        <DSCard frame="bars" density="compact" title="Recently earned" actions={(
            <DSButton appearance="text" tone="primary" size="sm" onClick={onGoStanding}>
              All badges
            </DSButton>
          )}>
          <button type="button" className="mm-badge-feature" onClick={() => onOpenBadge(latestBadge)}>
            <span className="mm-badge-feature__icon" aria-hidden="true"><BadgeIcon badge={latestBadge} size={40} /></span>
            <span className="mm-badge-feature__main">
              <span className="mm-badge-feature__title">
                {latestBadge.name}
                <DSBadge variant="success" size="sm">New</DSBadge>
              </span>
              <span className="mm-badge-feature__body">{latestBadge.description}</span>
            </span>
          </button>
          {olderBadges.length > 0 && (
            <div className="mm-badge-strip">
              {olderBadges.map((badge) => (
                <button key={badge.id} type="button" className="mm-badge-chip" onClick={() => onOpenBadge(badge)}>
                  <span className="mm-badge-chip__icon" aria-hidden="true"><BadgeIcon badge={badge} size={20} /></span>
                  <span className="mm-badge-chip__main">
                    <span className="mm-badge-chip__name">{badge.name}</span>
                    <span className="mm-badge-chip__earned">{formatMonth(badge.earned_at)}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </DSCard>
      )}

      <DSCard frame="bars" density="compact" title="Buy something" subtitle="Comes out of your credit balance." headerTone="strong" flush>
        {[
          { key: 'guestPass', label: 'Guest day pass', help: 'One friend, all day', price: '$15.00' },
          { key: 'donation', label: 'Send a donation', help: 'Goes to machine upkeep, snacks, supplies, and other expenses', price: 'Any' }
        ].map((item) => (
          <button key={item.key} type="button" className="mm-buy-row" onClick={() => onOpenPurchase(item.key)}>
            <span className="mm-buy-row__main">
              <span className="mm-buy-row__label">{item.label}</span>
              <span className="mm-buy-row__help">{item.help}</span>
            </span>
            <span className="mm-buy-row__price">{item.price}</span>
            <DSIcon name="chevronRight" size="sm" tone="muted" />
          </button>
        ))}
      </DSCard>

      <DSCard
        frame="bars"
        density="compact"
        flush
        title="Recent activity"
        actions={<span className="mm-aside-note">Running balance</span>}
        footer={transactions.length > 3 ? (
          <DSCardFooterButton onClick={onToggleActivity} showArrow={false}>
            {showAllActivity ? 'Show fewer' : `View all ${transactions.length} transactions`}
          </DSCardFooterButton>
        ) : null}
      >
        {transactionsLoading ? (
          <p className="mm-empty">Loading activity...</p>
        ) : transactions.length === 0 ? (
          <p className="mm-empty">Nothing yet. Your first credit shows up here.</p>
        ) : (
          <>
            {visibleActivity.map((txn) => {
              const amount = parseFloat(txn.amount);
              return (
                <div key={txn.id} className="mm-activity">
                  <span className="mm-activity__main">
                    <span className="mm-activity__description">{txn.description}</span>
                    <span className="mm-activity__when">{formatWhen(txn.created_at)}</span>
                  </span>
                  <span className="mm-activity__amounts">
                    <span className={`mm-activity__amount ${amount > 0 ? 'mm-activity__amount--credit' : ''}`}>
                      {signedMoney(amount)}
                    </span>
                    <span className="mm-activity__running">{money(txn.balanceAfter)}</span>
                  </span>
                </div>
              );
            })}
          </>
        )}
      </DSCard>
    </>
  );
}

export default HomeTab;
